import { useEffect, useState } from 'react';

import {
  Card,
  Divider,
  NativeSelect,
  Skeleton,
  Stack,
  Typography,
  Button,
  Box,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { debounce } from 'lodash';

import { getPatientInfo } from 'apis/admin';
import useI18n from 'hooks/useI18n';
import useNotification from 'hooks/useNotification';
import useStudent from 'store/student/useStudent';
import usePatient from 'store/patient/usePatient';

import PatientInfoItem from './PatientInfoItem';
import useSurvey from 'store/survey/useSurvey';

import no_profile from 'assets/no_profile.svg';
import SmartnurseIcon from 'assets/smartnurse-icon.svg';
import ToggleDrawerButton from 'components/ToggleDrawerButton';

interface Props {
  menuDrawerWidth: number;
  setMenuDrawerWidth: (menuDrawerWidth: number) => void;
}

const PatientInfo = (props: Props) => {
  const { menuDrawerWidth, setMenuDrawerWidth } = props;
  const isOpen = menuDrawerWidth === 300;
  const i18n = useI18n();
  const { onFail } = useNotification();
  const { student_name: userName } = useStudent();
  const { onUpdateSurveyType } = useSurvey();
  const { patient, patientInfo, onSelectedPatientInfo } = usePatient();

  const [width, setWidth] = useState(window.innerWidth);

  const handleResize = debounce(() => {
    setWidth(window.innerWidth);
  }, 200);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (width < 1194) {
      setMenuDrawerWidth(52);
    } else {
      setMenuDrawerWidth(300);
    }
  }, [width]);

  // 바코드 모달창 만들기

  const onClickModal = (label: string) => {
    onUpdateSurveyType(label);
  };

  // 부진단 코드 index
  const [diseaseSubIndex, setDiseaseSubIndex] = useState(0);

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientInfo({ patient_id: patient.patient_id })
      .then(({ data }) => {
        onSelectedPatientInfo(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  if (!patientInfo) {
    return <Skeleton variant="rectangular" sx={{ flex: 1 }} />;
  }
  const {
    // column1
    patient_id,
    name,
    age,
    gender,
    blood,
    // column2
    height,
    weight,
    admin_hod,
    admin_pod,
    // column3
    department,
    ward,
    room,
    main_doctor,
    // column4
    disease_main,
    disease_sub,
    patient_virtual_id,
  } = patientInfo;

  // 부진단 코드 선택 UI
  const DiseaseSubCode = () => (
    <NativeSelect
      value={diseaseSubIndex}
      onChange={e => setDiseaseSubIndex(Number(e.target.value))}
      inputProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
      sx={{ fontSize: 14, lineHeight: '18px' }}
    >
      {disease_sub?.map(({ disease_id }, index) => (
        <option key={disease_id} value={index}>
          {disease_id}
        </option>
      ))}
    </NativeSelect>
  );

  const convertHeight = (heightStr: string) => {
    // "160cm"에서 숫자만 추출
    const cmValue = parseFloat(heightStr);
    if (isNaN(cmValue)) return heightStr;
    // cm를 인치로 변환
    const totalInches = cmValue / 2.54;
    // 전체 인치에서 피트와 남은 인치 계산
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return `${cmValue}cm (${feet}’${inches}”)`;
  };

  const convertWeight = (weightStr: string) => {
    // "52kg"에서 숫자만 추출
    const kgValue = parseFloat(weightStr);
    if (isNaN(kgValue)) return weightStr;
    // kg를 파운드로 변환
    const pounds = kgValue * 2.20462;
    return `${kgValue}kg (${pounds.toFixed(1)} lbs)`;
  };

  const toggleDrawer = () => {
    setMenuDrawerWidth(isOpen ? 52 : 300);
  };

  const headerHeight = 50;

  return (
    <div style={{ position: 'relative', zIndex: 1000 }}>
      <ToggleDrawerButton
        isOpen={isOpen}
        openWidth={300}
        closedWidth={52}
        onToggle={toggleDrawer}
        top={(headerHeight - 30) / 2}
      />

      {!isOpen ? (
        <Box sx={{ height: 1 }} />
      ) : (
        <>
          {/* 상단 로고 & 헤더 */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: `${headerHeight}px`,
            }}
          >
            <Box
              height={36}
              component="img"
              alt="SmartNurse"
              src={SmartnurseIcon}
              sx={{ objectFit: 'contain' }}
            />
          </Box>

          <Card
            component="section"
            sx={{
              p: 2,
              overflow: 'visible',
              justifyContent: 'space-between',
              gap: { xs: 1.25, xl: 2.5 },
              backgroundColor: '#B1CCF5',
            }}
          >
            <Stack spacing={1.25} sx={{ mb: '10px' }}>
              <Box
                height={183}
                component="img"
                alt="SmartNurse"
                src={patient?.photo !== null ? patient?.photo : no_profile}
                sx={{ objectFit: 'contain', mb: '20px', borderRadius: '8px' }}
              />
            </Stack>

            {/* 컬럼 1 */}
            <Stack spacing={1.25} sx={{ mb: '10px' }}>
              <PatientInfoItem title="등록번호" content={patient_virtual_id} />
              <PatientInfoItem title="이름" content={name} />
              <PatientInfoItem title="나이" content={age} />
              <PatientInfoItem
                title="성별"
                content={i18n(`GENDER.${gender}`)}
              />
            </Stack>

            <Divider orientation="vertical" />

            {/* 컬럼 2 */}
            <Stack spacing={1.25} sx={{ my: '10px' }}>
              <PatientInfoItem title="혈액형" content={blood} />
              <PatientInfoItem title="키" content={convertHeight(height)} />
              <PatientInfoItem title="체중" content={convertWeight(weight)} />
              <PatientInfoItem title="HOD" content={admin_hod} />
              <PatientInfoItem title="POD" content={admin_pod} />
            </Stack>

            <Divider orientation="vertical" />

            {/* 컬럼 3 */}
            <Stack spacing={1.25} sx={{ my: '10px' }}>
              <PatientInfoItem title="부서" content={department} />
              <PatientInfoItem title="담당교수" content={main_doctor} />
              <PatientInfoItem title="병동" content={ward} />
              <PatientInfoItem title="병실" content={room} />
              <PatientInfoItem title="담당간호사" content={userName} />
            </Stack>

            <Divider orientation="vertical" />

            {/* 컬럼 4 */}
            <Stack spacing={1.25} sx={{ my: '10px' }}>
              <PatientInfoItem
                title="주진단코드"
                content={disease_main.disease_id}
              />
              <PatientInfoItem
                title="주진단명"
                content={disease_main.disease_eng}
              />
              <PatientInfoItem
                title="부진단코드"
                content={
                  disease_sub.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'right',
                        width: '140px',
                      }}
                    >
                      <DiseaseSubCode />
                    </Box>
                  ) : null
                }
              />
              <PatientInfoItem
                title="부진단명"
                content={disease_sub[diseaseSubIndex]?.disease_eng}
              />
            </Stack>
          </Card>

          {/* 하단 버튼 */}
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              size="small"
              sx={{
                mt: '20px',
                mb: '5px',
                backgroundColor: '#2D73DC',
                color: 'white',
              }}
              onClick={() => onClickModal('환자 라벨 인쇄')}
            >
              환자 라벨 인쇄
            </Button>
          </Box>
        </>
      )}
    </div>
  );
};

export default PatientInfo;
